import { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { useAuth } from './contexts/AuthContext';
import { useUsage } from './hooks/useUsage';
import Header from './components/Header';
import LandingHero from './components/LandingHero';
import LoginPage from './components/LoginPage';
import KanbanBoard from './components/KanbanBoard';
import UseCasesTab from './components/UseCasesTab';
import HowItWorksTab from './components/HowItWorksTab';
import ImpactTab from './components/ImpactTab';
import DetailDrawer from './components/DetailDrawer';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function App() {
  const { user, session, loading: authLoading, signOut } = useAuth();
  const usage = useUsage(session);
  const [showLanding, setShowLanding] = useState(() => !sessionStorage.getItem('unvague_entered'));
  const [showLogin, setShowLogin] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [activeTab, setActiveTab] = useState('app');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState({});
  const [agentProgress, setAgentProgress] = useState({});
  const [failedAnalysis, setFailedAnalysis] = useState({});
  const [analysisLog, setAnalysisLog] = useState([]);
  const [batchProgress, setBatchProgress] = useState(null);
  const [rateLimitReset, setRateLimitReset] = useState(null);
  const ticketsRef = useRef([]);

  // Auth headers ref — always current, no stale closure issues
  const authHeadersRef = useRef({});
  useEffect(() => {
    authHeadersRef.current = session?.access_token
      ? { Authorization: `Bearer ${session.access_token}` }
      : {};
  }, [session]);

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => { ticketsRef.current = tickets; }, [tickets]);

  const fetchTickets = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const res = await axios.get(`${API}/tickets`, { headers: authHeadersRef.current });
      if (Array.isArray(res.data)) setTickets(res.data);
    } catch (e) {
      console.error('Failed to fetch tickets', e);
    }
  }, [session]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const createTicket = async (title, text) => {
    const res = await axios.post(`${API}/tickets`, { title, text }, { headers: authHeadersRef.current });
    const ticket = res.data;
    setTickets(prev => [...prev, ticket]);
    return ticket;
  };

  const simulateProgress = (ticketId) => {
    setAgentProgress(p => ({ ...p, [ticketId]: { detection: 'running', assessment: 'pending', remediation: 'pending' } }));
    setTimeout(() => {
      setAgentProgress(p => ({ ...p, [ticketId]: { detection: 'done', assessment: 'running', remediation: 'pending' } }));
    }, 1500);
    setTimeout(() => {
      setAgentProgress(p => ({ ...p, [ticketId]: { detection: 'done', assessment: 'done', remediation: 'running' } }));
    }, 3000);
  };

  const analyzeTicket = useCallback(async (ticketId, text, title, autoOpen = false) => {
    setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, column: 'analyzing' } : t));
    setIsAnalyzing(p => ({ ...p, [ticketId]: true }));
    setFailedAnalysis(p => { const n = { ...p }; delete n[ticketId]; return n; });
    simulateProgress(ticketId);

    try {
      const res = await axios.post(`${API}/analyze`, { text, title, ticket_id: ticketId }, { headers: authHeadersRef.current });
      const analysis = res.data;
      const score = analysis.assessment?.ambiguityScore ?? 0;
      const column = score > 50 ? 'needs_clarification' : 'ready_for_dev';
      const analyzed_at = analysis.meta?.timestamp;

      const existingTicket = ticketsRef.current.find(t => t.id === ticketId) || {};
      const originalScore = existingTicket.originalScore ?? score;

      setTickets(prev => prev.map(t =>
        t.id === ticketId ? { ...t, column, analysis, analyzed_at, originalScore } : t
      ));
      setAgentProgress(p => ({ ...p, [ticketId]: { detection: 'done', assessment: 'done', remediation: 'done' } }));

      const logTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      setAnalysisLog(prev => [{
        id: `${ticketId}-${Date.now()}`, time: logTime, type: 'analyzed',
        title: existingTicket.title || title, score, riskLevel: analysis.assessment?.riskLevel || 'unknown',
      }, ...prev].slice(0, 25));

      if (autoOpen) {
        const base = ticketsRef.current.find(t => t.id === ticketId) || {};
        const updated = { ...base, column, analysis, analyzed_at, originalScore };
        setTimeout(() => setSelectedTicket(updated), 700);
      }

      return { riskLevel: analysis.assessment?.riskLevel || 'unknown' };
    } catch (e) {
      console.error('Analysis error', e);
      if (e.response && e.response.status === 429) {
        const resetIn = e.response.data?.detail?.reset_in_seconds || 3600;
        setRateLimitReset(Date.now() + resetIn * 1000);
        toast.error('Rate limit reached. 10 scans per hour.');
      } else {
        toast.error('Analysis failed. Please try again.');
      }
      setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, column: 'backlog' } : t));
      setFailedAnalysis(p => ({ ...p, [ticketId]: true }));
    } finally {
      setIsAnalyzing(p => ({ ...p, [ticketId]: false }));
    }
  }, []);

  const batchScan = useCallback(async (rawText) => {
    const parseBlocks = (raw) => {
      if (/^-{3,}\s*$/m.test(raw)) {
        return raw.split(/^-{3,}\s*$/m).map(b => b.trim()).filter(b => b.length > 0);
      }
      const lines = raw.split('\n');
      if (lines.some(l => /^\d+[.)]\s/.test(l))) {
        const result = [];
        let current = [];
        for (const line of lines) {
          if (/^\d+[.)]\s/.test(line) && current.length > 0) {
            const block = current.join('\n').trim();
            if (block) result.push(block);
            current = [line];
          } else {
            current.push(line);
          }
        }
        const last = current.join('\n').trim();
        if (last) result.push(last);
        return result;
      }
      return raw.trim() ? [raw.trim()] : [];
    };

    const blocks = parseBlocks(rawText).map(block => {
      const cleanText = block.replace(/^\d+[.)]\s+/, '');
      const words = cleanText.split(/\s+/);
      const titleWords = words.slice(0, 7).join(' ');
      const title = (words.length > 7 ? titleWords + '...' : titleWords);
      return { title: title.charAt(0).toUpperCase() + title.slice(1), text: cleanText };
    });

    if (blocks.length === 0) {
      toast.error('No valid requirement blocks found.');
      return;
    }

    const created = [];
    for (const block of blocks) {
      try {
        const ticket = await createTicket(block.title, block.text);
        created.push(ticket);
      } catch (e) { console.error('Failed to create batch ticket', e); }
    }

    if (created.length === 0) { toast.error('Failed to create tickets.'); return; }

    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    setBatchProgress({ current: 1, completed: 0, total: created.length, counts: { ...counts } });

    for (let i = 0; i < created.length; i++) {
      setBatchProgress(prev => prev ? { ...prev, current: i + 1, completed: i } : null);
      try {
        const result = await analyzeTicket(created[i].id, created[i].text, created[i].title, false);
        const rl = result?.riskLevel;
        if (rl && counts[rl] !== undefined) counts[rl]++;
        setBatchProgress(prev => prev ? { ...prev, completed: i + 1, counts: { ...counts } } : null);
      } catch (e) { console.error('Batch analysis failed for:', created[i].title, e); }
    }

    setBatchProgress(null);
    const parts = [];
    if (counts.critical > 0) parts.push(`${counts.critical} critical`);
    if (counts.high > 0) parts.push(`${counts.high} high`);
    if (counts.medium > 0) parts.push(`${counts.medium} medium`);
    if (counts.low > 0) parts.push(`${counts.low} low`);
    const riskSummary = parts.length > 0 ? parts.join(', ') : 'all low risk';
    toast.success(`Batch scan complete — ${created.length} tickets analyzed. ${riskSummary}.`, { duration: 7000 });
  }, [analyzeTicket]);

  const tryUseCase = useCallback(async (title, text) => {
    setActiveTab('app');
    await new Promise(r => setTimeout(r, 100));
    try {
      const ticket = await createTicket(title, text);
      await analyzeTicket(ticket.id, text, title, true);
    } catch (e) {
      toast.error('Demo failed. Please try again.');
    }
  }, [analyzeTicket]);

  const moveTicket = async (ticketId, newColumn) => {
    setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, column: newColumn } : t));
    try { await axios.put(`${API}/tickets/${ticketId}`, { column: newColumn }, { headers: authHeadersRef.current }); }
    catch (e) { console.error(e); }
  };

  const acceptRewrite = async (ticket) => {
    const newText = ticket.analysis?.remediation?.rewrite;
    if (!newText) return;
    const updated = { ...ticket, text: newText, column: 'refined' };
    setTickets(prev => prev.map(t => t.id === ticket.id ? updated : t));
    setSelectedTicket(null);
    toast.success('Rewrite accepted — ticket moved to Refined');
    const logTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    setAnalysisLog(prev => [{
      id: `${ticket.id}-accept-${Date.now()}`, time: logTime, type: 'rewrite_accepted',
      title: ticket.title, score: ticket.analysis?.assessment?.ambiguityScore,
    }, ...prev].slice(0, 25));
    try { await axios.put(`${API}/tickets/${ticket.id}`, { text: newText, column: 'refined' }, { headers: authHeadersRef.current }); }
    catch (e) { console.error(e); }
  };

  const reAnalyze = useCallback(async (ticket) => {
    setSelectedTicket(null);
    await analyzeTicket(ticket.id, ticket.text, ticket.title, true);
  }, [analyzeTicket]);

  const deleteTicket = async (ticketId) => {
    setTickets(prev => prev.filter(t => t.id !== ticketId));
    if (selectedTicket?.id === ticketId) setSelectedTicket(null);
    try { await axios.delete(`${API}/tickets/${ticketId}`, { headers: authHeadersRef.current }); }
    catch (e) { console.error(e); }
  };

  const enterApp = () => {
    sessionStorage.setItem('unvague_entered', '1');
    setShowLanding(false);
    if (!user) setShowLogin(true);
  };

  const quickAnalyze = async (text) => {
    sessionStorage.setItem('unvague_entered', '1');
    setShowLanding(false);
    if (!user) {
      setShowLogin(true);
      sessionStorage.setItem('unvague_pending_text', text);
      return;
    }
    const words = text.split(/\s+/);
    const titleWords = words.slice(0, 7).join(' ');
    const title = (words.length > 7 ? titleWords + '...' : titleWords);
    const capitalTitle = title.charAt(0).toUpperCase() + title.slice(1);
    try {
      const ticket = await createTicket(capitalTitle, text);
      await analyzeTicket(ticket.id, text, capitalTitle, true);
    } catch (e) {
      toast.error('Analysis failed. Please try again.');
    }
  };

  const goToLanding = () => {
    sessionStorage.removeItem('unvague_entered');
    setShowLanding(true);
    setShowLogin(false);
  };

  // After login, process any pending quick-analyze text
  useEffect(() => {
    if (user && !authLoading) {
      setShowLogin(false);
      const pendingText = sessionStorage.getItem('unvague_pending_text');
      if (pendingText) {
        sessionStorage.removeItem('unvague_pending_text');
        quickAnalyze(pendingText);
      }
    }
  }, [user, authLoading]);

  // Loading state
  if (authLoading) {
    return (
      <div className="app-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
      </div>
    );
  }

  // Landing hero (visible to everyone)
  if (showLanding) {
    return (
      <div className="app-root">
        <Toaster theme="dark" position="bottom-right" richColors />
        <LandingHero onEnterApp={enterApp} onQuickAnalyze={quickAnalyze} />
      </div>
    );
  }

  // Login page (not authenticated)
  if (!user || showLogin) {
    return (
      <div className="app-root">
        <Toaster theme="dark" position="bottom-right" richColors />
        <LoginPage />
        <button
          onClick={goToLanding}
          style={{
            position: 'fixed', top: 20, left: 20, background: 'none', border: 'none',
            color: '#52525b', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          &larr; Back
        </button>
      </div>
    );
  }

  return (
    <div className="app-root">
      <Toaster theme="dark" position="bottom-right" richColors />
      <Header onLogoClick={goToLanding} user={user} usage={usage} onSignOut={signOut} />
      <nav className="tab-nav" data-testid="tab-navigation">
        {[
          { id: 'app', label: 'App' },
          { id: 'use-cases', label: 'Use Cases' },
          { id: 'how-it-works', label: 'How It Works' },
          { id: 'impact', label: 'Impact' },
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            className={`tab-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="main-area">
        {activeTab === 'app' && (
          <KanbanBoard
            tickets={tickets}
            isAnalyzing={isAnalyzing}
            agentProgress={agentProgress}
            failedAnalysis={failedAnalysis}
            batchProgress={batchProgress}
            rateLimitReset={rateLimitReset}
            onAnalyze={analyzeTicket}
            onCardClick={(t) => t.analysis && setSelectedTicket(t)}
            onMove={moveTicket}
            onNew={createTicket}
            onDelete={deleteTicket}
            onBatchScan={batchScan}
          />
        )}
        {activeTab === 'use-cases' && <UseCasesTab onTryUseCase={tryUseCase} />}
        {activeTab === 'how-it-works' && <HowItWorksTab />}
        {activeTab === 'impact' && <ImpactTab tickets={tickets} analysisLog={analysisLog} />}
      </main>

      {selectedTicket && (
        <DetailDrawer
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onAcceptRewrite={acceptRewrite}
          onReAnalyze={reAnalyze}
        />
      )}
    </div>
  );
}
